/**
 * Le domaine de l'app, en un fichier.
 *
 * Tout part d'un principe : le coach remplit une fois par semaine, le client
 * consomme tous les jours. Aucun objet ici ne demande une action quotidienne
 * du coach — sinon le suivi s'arrête au bout de trois semaines.
 */

/** Lundi = 0. On ne suit jamais la convention de `Date.getDay()` (dimanche = 0). */
export const JOURS = [
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
  "dimanche",
] as const

/** Étiquettes de la grille hebdomadaire. « Ma » et « Me » pour lever l'ambiguïté. */
export const JOURS_COURTS = ["L", "Ma", "Me", "J", "V", "S", "D"] as const

/**
 * Trois états, pas deux.
 *
 * `attente` (pas encore répondu) et `manque` (répondu : pas fait) sont deux
 * informations différentes. Les confondre reviendrait à lire un silence comme
 * un échec — et le coach écrirait son mot du dimanche sur du vent.
 */
export type Statut = "attente" | "fait" | "manque"

/**
 * Le « comment faire » d'une consigne.
 *
 * La différence entre une liste sur papier et un outil de coach : le client ne
 * voit pas seulement *qu'un* exercice est prévu, il sait *comment* le réaliser.
 * C'est la voix du coach déposée à côté de la consigne, pour quand il n'est pas
 * là. Optionnel : une marche n'en a pas besoin, un gainage oui.
 */
export type CommentFaire = {
  /** Une phrase qui cadre le mouvement. Optionnelle. */
  intro?: string
  /** Les points clés, tels que le coach les rappellerait — l'essentiel à ne pas rater. */
  points: string[]
}

/**
 * Une étape d'un déroulé guidé — un moment de la séance, pas une case à cocher.
 *
 * Le client la vit une à la fois, en grand : ce qu'il fait *maintenant*, et rien
 * d'autre. `duree` n'est là que pour les étapes qui se tiennent dans le temps
 * (un gainage, un étirement) — le minuteur n'apparaît que pour elles.
 */
export type EtapeSeance = {
  titre: string
  /** Ce qu'il faut savoir pour bien vivre ce moment-là. Optionnel. */
  detail?: string
  /** Durée en secondes quand l'étape se tient dans le temps. Absente sinon. */
  duree?: number
}

/**
 * Le déroulé guidé d'une consigne : la séance *vécue*, étape par étape.
 *
 * Là où le « comment faire » (`CommentFaire`) est un rappel qu'on lit, le
 * déroulé est une séance qu'on *traverse* — le geste qui sépare Cairn d'une
 * liste sur papier. Optionnel : une marche n'en a pas besoin, une séance
 * autonome oui.
 */
export type Deroule = {
  /** Une phrase pour se mettre dedans avant de démarrer. Optionnelle. */
  intro?: string
  etapes: EtapeSeance[]
}

/**
 * Une consigne de la semaine, posée sur des jours précis.
 *
 * Le coach choisit les jours une fois le dimanche ; le client répond chaque
 * jour concerné. C'est ce qui rend l'app quotidienne sans rien coûter de plus
 * au coach.
 */
export type Tache = {
  id: string
  label: string
  /** Jours où la consigne s'applique, lundi = 0. */
  jours: number[]
  /** Réponse du client, par jour. Un jour absent vaut `attente`. */
  statuts: Record<number, Statut>
  /** Comment réaliser le mouvement. Absent quand la consigne se passe d'explication. */
  comment?: CommentFaire
  /** Le déroulé guidé, quand la consigne se vit comme une séance à traverser. */
  deroule?: Deroule
}

/** Un repère chiffré. `unite` reste optionnelle : « 4 / 5 » n'a pas d'unité. */
export type Repere = {
  id: string
  label: string
  valeur: string
  unite?: string
}

/**
 * Un moment prévu de la séance à venir, tel que le coach l'a pensé.
 *
 * Pas une case à cocher : une intention posée d'avance, pour que la cliente
 * arrive en sachant *où l'on va*. `detail` reste optionnel — un titre suffit
 * parfois à situer le moment.
 */
export type MomentSeance = {
  titre: string
  detail?: string
}

/**
 * La prochaine séance *avec le coach* — le cœur du produit premium.
 *
 * C'est le rendez-vous à domicile qui justifie Cairn ; l'app ne doit pas le
 * réduire à une date. Tout ce qui suit `quand`/`lieu` est optionnel : la
 * cliente peut n'avoir qu'un horaire, ou le brief complet que le coach a
 * préparé. Chaque pièce absente s'efface proprement, aucune ne se devine.
 */
export type Seance = {
  /** Déjà formaté : l'app n'a pas de calendrier, elle affiche ce qu'on lui donne. */
  quand: string
  lieu: string
  /** Durée annoncée, ex. « 45 min ». Optionnelle. */
  duree?: string
  /** Le thème de la séance en deux mots, ex. « Dos et posture ». Optionnel. */
  focus?: string
  /** La voix du coach : ce qu'on va travailler et pourquoi. Optionnelle. */
  intention?: string
  /** Le déroulé prévu, moment par moment. Vide quand le coach ne l'a pas détaillé. */
  auProgramme?: MomentSeance[]
  /** Ce qu'il vaut mieux avoir sous la main. Vide si rien de particulier. */
  aPreparer?: string[]
}

/** Le contenu bonus. Écrit une fois, attribué en un clic. */
export type Fiche = {
  titre: string
  resume: string
}

/**
 * Un message du fil — la première fois que le client a une *voix*.
 *
 * Tout le reste de l'app est à sens unique : le coach dépose (mot, consignes,
 * réactions), le client coche. Le fil renverse ça — c'est le canal « entre les
 * deux », là où la cliente peut poser une question, signaler une gêne, dire
 * comment la semaine s'est passée, et sentir que quelqu'un l'écoute quand le
 * coach n'est pas là. `quand` est déjà formaté : l'app n'a pas de calendrier.
 */
export type Message = {
  id: string
  auteur: "coach" | "client"
  texte: string
  /** Déjà lisible, ex. « Dimanche », « Jeudi, 18 h ». */
  quand: string
}

/** Ce que le coach publie chaque semaine pour un client. */
export type Semaine = {
  /** Libellé lisible, ex. « Semaine du 10 août ». */
  libelle: string
  /** Étiquette courte pour la frise du parcours, ex. « 10/8 ». */
  court: string
  /** Le mot du coach. C'est la pièce maîtresse : il passe avant les chiffres. */
  mot: string
  taches: Tache[]
  reperes: Repere[]
  fiche?: Fiche
}

/**
 * Une semaine close, figée dans le parcours du client.
 *
 * Le dashboard ne montre que l'instant présent ; le parcours, lui, raconte la
 * durée. Chaque semaine passée garde son bilan tel qu'il était à la clôture —
 * on ne recalcule pas le passé, on le lit.
 */
export type SemainePassee = {
  id: string
  /** « Semaine du 3 août ». */
  libelle: string
  /** Étiquette courte pour la frise, ex. « 3/8 ». */
  court: string
  /** Créneaux tenus / prévus, figés à la clôture. */
  fait: number
  total: number
  /** Un fait marquant laissé par le coach, optionnel. */
  note?: string
}

export type Client = {
  id: string
  prenom: string
  nom: string
  coach: string
  prochaineSeance: Seance
  semaine: Semaine
  /** Les semaines closes, de la plus ancienne à la plus récente. */
  historique: SemainePassee[]
  /** Le fil avec le coach, du plus ancien au plus récent. Le dernier message du
   *  coach est, par continuité, le « mot de la semaine » lu sur le dashboard. */
  fil: Message[]
}

/** Compte les réponses d'une liste de consignes, tous jours confondus. */
export function compter(taches: Tache[]) {
  let fait = 0
  let manque = 0
  let attente = 0

  for (const tache of taches) {
    for (const jour of tache.jours) {
      const statut = tache.statuts[jour] ?? "attente"
      if (statut === "fait") fait += 1
      else if (statut === "manque") manque += 1
      else attente += 1
    }
  }

  return { fait, manque, attente, total: fait + manque + attente }
}

/**
 * Le bilan de la semaine *vu du jour où l'on regarde*.
 *
 * `compter` ne sait pas quel jour on est : il mêle le passé et l'à-venir. Or la
 * sensation d'avoir *tenu* sa semaine ne se juge pas sur les jours pas encore
 * arrivés — seulement sur ce qui était dû. D'où `aRattraper` : les créneaux
 * échus (jour strictement passé) restés sans réponse. C'est la seule chose qui
 * empêche d'être « à jour » ; le reste (aujourd'hui, l'à-venir) n'est pas un
 * retard, juste ce qui reste devant soi.
 */
export type BilanSemaine = {
  /** Créneaux prévus sur toute la semaine. */
  total: number
  fait: number
  manque: number
  /** Créneaux échus et restés sans réponse — ce qu'il reste à rattraper. */
  aRattraper: number
}

export function bilanSemaine(taches: Tache[], aujourdhui: number): BilanSemaine {
  let total = 0
  let fait = 0
  let manque = 0
  let aRattraper = 0

  for (const tache of taches) {
    for (const jour of tache.jours) {
      total += 1
      const statut = tache.statuts[jour] ?? "attente"
      if (statut === "fait") fait += 1
      else if (statut === "manque") manque += 1
      else if (jour < aujourdhui) aRattraper += 1
    }
  }

  return { total, fait, manque, aRattraper }
}

/**
 * Le parcours : la durée racontée, pas l'instant.
 *
 * Une étape par semaine, de la plus ancienne à la semaine en cours (marquée à
 * part car pas encore close). Les agrégats sont pensés pour *encourager sans
 * juger* : les séances honorées ne se perdent jamais (l'effort est en banque),
 * et la régularité se calcule sur les semaines **closes** — la semaine en cours,
 * forcément incomplète, ne doit pas tirer le chiffre vers le bas.
 */
export type EtapeParcours = SemainePassee & { enCours: boolean }

export type Parcours = {
  /** De la plus ancienne à la semaine en cours (incluse). */
  etapes: EtapeParcours[]
  /** Cumul des créneaux tenus, semaine en cours comprise — jamais remis à zéro. */
  seancesHonorees: number
  /** Nombre de semaines où le client a été suivi (en cours comprise). */
  semainesSuivies: number
  /** Régularité sur les semaines closes, en pourcentage arrondi. */
  regularite: number
  /** Semaines closes tenues de bout en bout. */
  semainesTenues: number
  /**
   * La série en cours : semaines *actives* consécutives (au moins un créneau
   * tenu), la plus récente comprise. Le ressort d'engagement rendu visible —
   * ce qui donne envie de ne pas casser la chaîne. La semaine en cours pas
   * encore entamée ne rompt pas la série : elle attend, elle ne punit pas.
   */
  serie: number
}

export function construireParcours(
  historique: SemainePassee[],
  courante: SemainePassee
): Parcours {
  const etapes: EtapeParcours[] = [
    ...historique.map((s) => ({ ...s, enCours: false })),
    { ...courante, enCours: true },
  ]

  const closes = etapes.filter((e) => !e.enCours)
  const seancesHonorees = etapes.reduce((n, e) => n + e.fait, 0)
  const creneauxClos = closes.reduce((n, e) => n + e.total, 0)
  const faitClos = closes.reduce((n, e) => n + e.fait, 0)

  /* La série : on remonte depuis la semaine la plus récente tant qu'elle est
     active. La semaine en cours encore vide n'interrompt pas — elle patiente. */
  let serie = 0
  for (let i = etapes.length - 1; i >= 0; i--) {
    const e = etapes[i]
    if (e.fait > 0) serie += 1
    else if (e.enCours) continue
    else break
  }

  return {
    etapes,
    seancesHonorees,
    semainesSuivies: etapes.length,
    regularite: creneauxClos > 0 ? Math.round((faitClos / creneauxClos) * 100) : 0,
    semainesTenues: closes.filter((e) => e.total > 0 && e.fait === e.total).length,
    serie,
  }
}
